import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { MEMBERS_CLASSES_ENDPOINT } from "../../config/urls";

export class ApiError extends Error {
  constructor(status, data) {
    super(`Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.data = data || {};
  }
}

/** { class: [1, 2], q: "pune", city: "" } -> "?class=1&class=2&q=pune" (blank values are left out). */
export const buildQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, item));
    } else if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

const firstText = (value) => (Array.isArray(value) ? String(value[0] ?? "") : value == null ? "" : String(value));

/** DRF validation errors { field: ["message"] } -> { field: "message" }. */
export const fieldErrorsFrom = (error) => {
  const out = {};
  if (!error || error.status !== 400 || typeof error.data !== "object") return out;
  Object.entries(error.data).forEach(([key, value]) => {
    out[key] = firstText(value);
  });
  return out;
};

/** A readable one-liner for an error that has no field to attach to. */
export const messageFrom = (error, fallback = "Something went wrong. Please try again.") => {
  if (!error) return fallback;
  if (error.status === 403) return "You do not have permission to do that.";
  if (error.status === 404) return "This item could not be found.";
  const data = error.data || {};
  if (typeof data.detail === "string") return data.detail;
  const first = Object.values(data)[0];
  return first ? firstText(first) : fallback;
};

const readBody = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (e) {
    return { detail: text.slice(0, 200) };
  }
};

/**
 * fetch helpers for the Members API. Same convention as the other tenant pages:
 * a fresh Bearer token from useAuth().getValidToken() plus the X-Tenant-ID header.
 * The returned object is stable for the life of the component, so it is safe in effect dependencies.
 */
export const useMembersApi = () => {
  const { getValidToken } = useAuth();
  // AuthProvider makes a new getValidToken on every render; keep the latest in a ref so the
  // returned api object never changes identity (it sits in many effect dependency lists).
  const tokenSource = useRef(getValidToken);
  tokenSource.current = getValidToken;

  const authHeaders = useCallback(async (extra = {}) => {
    const token = await tokenSource.current();
    return {
      Authorization: `Bearer ${token}`,
      "X-Tenant-ID": localStorage.getItem("tenant") || "",
      ...extra,
    };
  }, []);

  const request = useCallback(
    async (url, { method = "GET", body, signal } = {}) => {
      const response = await fetch(url, {
        method,
        signal,
        headers: await authHeaders(body !== undefined ? { "Content-Type": "application/json" } : {}),
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      const data = await readBody(response);
      if (!response.ok) throw new ApiError(response.status, data);
      return data;
    },
    [authHeaders],
  );

  /** multipart upload; the browser sets the Content-Type (with the boundary) itself. */
  const upload = useCallback(
    async (url, file, signal) => {
      const form = new FormData();
      form.append("file", file);
      const response = await fetch(url, { method: "POST", signal, headers: await authHeaders(), body: form });
      const data = await readBody(response);
      if (!response.ok) throw new ApiError(response.status, data);
      return data;
    },
    [authHeaders],
  );

  /** Fetch a file with auth, then hand it to the browser as a download. */
  const download = useCallback(
    async (url, fallbackName = "download") => {
      const response = await fetch(url, { headers: await authHeaders() });
      if (!response.ok) throw new ApiError(response.status, await readBody(response));
      const blob = await response.blob();
      const match = /filename="?([^";]+)"?/i.exec(response.headers.get("Content-Disposition") || "");
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = match ? match[1] : fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    },
    [authHeaders],
  );

  return useMemo(() => ({ request, upload, download }), [request, upload, download]);
};

/** The membership classes for the Class dropdowns, loaded once. */
export const useMembershipClasses = (api) => {
  const [classes, setClasses] = useState([]);
  useEffect(() => {
    let active = true;
    api
      .request(MEMBERS_CLASSES_ENDPOINT)
      .then((data) => active && setClasses(Array.isArray(data) ? data : data.results || []))
      .catch(() => active && setClasses([]));
    return () => {
      active = false;
    };
  }, [api]);
  return classes;
};
